import { useState } from 'react';
import { Gift, Users, DollarSign, TrendingUp, Copy, Check, Sparkles, Shield, Zap } from 'lucide-react';
import './ReferralPage.css';

const ReferralPage = () => {
  const [copied, setCopied] = useState(false);
  const referralLink = 'https://omnibridge.com/ref/YOUR_CODE';

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tiers = [
    { level: 'Bronze', referrals: '1-10', commission: '1%', color: '#cd7f32' },
    { level: 'Silver', referrals: '11-50', commission: '1.5%', color: '#c0c0c0' },
    { level: 'Gold', referrals: '51-100', commission: '2%', color: '#ffd700' },
    { level: 'Platinum', referrals: '100+', commission: '2.5%', color: '#e5e4e2' },
  ];

  const stats = [
    { icon: Users, value: '50K+', label: 'Active Referrers' },
    { icon: DollarSign, value: '$2.5M+', label: 'Rewards Paid' },
    { icon: TrendingUp, value: '$500', label: 'Avg. Monthly' },
  ];

  const howItWorks = [
    { step: 1, title: 'Get Your Link', desc: 'Sign up and get your unique referral link' },
    { step: 2, title: 'Share', desc: 'Share with friends, followers, or your community' },
    { step: 3, title: 'Earn', desc: 'Earn commission on every trade they make' },
  ];

  return (
    <div className="referral-page">
      {/* Background */}
      <div className="referral-bg">
        <div className="confetti"></div>
        <div className="glow-orb"></div>
      </div>

      <div className="referral-container">
        {/* Hero */}
        <section className="referral-hero">
          <div className="hero-badge">
            <Sparkles size={18} />
            <span>Referral Program</span>
          </div>
          <h1 className="referral-title">
            Earn Up To <span className="gradient-text">2.5%</span> Commission
          </h1>
          <p className="referral-subtitle">
            Invite your friends to Omini Bridge and earn passive income on every exchange they make. 
            The more they trade, the more you earn.
          </p>

          {/* Referral Link Box */}
          <div className="referral-box">
            <div className="referral-input-group">
              <input 
                type="text" 
                value={referralLink} 
                readOnly 
                className="referral-input"
              />
              <button className="copy-link-btn" onClick={copyLink}>
                {copied ? <Check size={20} /> : <Copy size={20} />}
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
            <p className="referral-hint">Sign in to get your personal referral link</p>
          </div>
        </section>

        {/* Stats */}
        <section className="referral-stats">
          {stats.map((stat, index) => (
            <div key={index} className="referral-stat-card" style={{ animationDelay: `${index * 0.1}s` }}>
              <stat.icon size={32} className="stat-icon-ref" />
              <span className="stat-value-ref">{stat.value}</span>
              <span className="stat-label-ref">{stat.label}</span>
            </div>
          ))}
        </section>

        {/* How It Works */}
        <section className="how-it-works">
          <h2>How It Works</h2>
          <div className="steps-container">
            {howItWorks.map((item, index) => (
              <div key={index} className="step-item" style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="step-circle">{item.step}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Tiers */}
        <section className="tiers-section">
          <h2>Commission Tiers</h2>
          <p className="tiers-subtitle">Climb the ranks and earn more</p>
          <div className="tiers-grid">
            {tiers.map((tier, index) => (
              <div 
                key={index} 
                className="tier-card"
                style={{ 
                  animationDelay: `${index * 0.1}s`,
                  borderColor: tier.color
                }}
              >
                <div className="tier-badge" style={{ background: tier.color }}>
                  {tier.level}
                </div>
                <div className="tier-commission" style={{ color: tier.color }}>
                  {tier.commission}
                </div>
                <p className="tier-range">{tier.referrals} referrals</p>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="ref-features">
          <div className="ref-feature">
            <Gift size={40} className="ref-feature-icon" />
            <h3>Lifetime Earnings</h3>
            <p>Earn commission on every trade your referrals make, forever</p>
          </div>
          <div className="ref-feature">
            <Zap size={40} className="ref-feature-icon" />
            <h3>Instant Payouts</h3>
            <p>Withdraw your earnings instantly to your wallet</p>
          </div>
          <div className="ref-feature">
            <Shield size={40} className="ref-feature-icon" />
            <h3>Real-time Tracking</h3>
            <p>Track your referrals and earnings in real-time</p>
          </div>
        </section>

        {/* CTA */}
        <section className="referral-cta">
          <h2>Start Earning Today</h2>
          <p>Join our referral program and turn your network into income</p>
          <button className="start-earning-btn">
            <Gift size={20} />
            Get Started
          </button>
        </section>
      </div>
    </div>
  );
};

export default ReferralPage;
